import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '@/services/api';
import { User, Shop, ShopConfig, LoginRequest, GenerateCodeRequest, ApiResponse } from '@/services/types';
import { API_ENDPOINTS, STORAGE_KEYS } from '@/utils/constants';
import { closePosSession } from '@/features/session/posSession';

/**
 * Auth State Interface
 */
interface AuthState {
    user: User | null;
    selectedShop: Shop | null;
    shopConfig: ShopConfig | null;
    shops: Shop[];
    isAuthenticated: boolean;
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
}

/**
 * Initial State
 */
const initialState: AuthState = {
    user: null,
    selectedShop: null,
    shopConfig: null,
    shops: [],
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    error: null,
};

/**
 * Async Thunks
 */

// Generate OTP
export const generateCode = createAsyncThunk(
    'auth/generateCode',
    async (data: GenerateCodeRequest, { rejectWithValue }) => {
        try {
            const response = await api.post<ApiResponse<{ phone: string; countryCode: string }>>(
                API_ENDPOINTS.GENERATE_CODE,
                data
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Login with OTP
export const login = createAsyncThunk(
    'auth/login',
    async (data: LoginRequest, { rejectWithValue }) => {
        try {
            const response = await api.post<ApiResponse<User>>(
                API_ENDPOINTS.LOGIN,
                data
            );

            const user = response.data.data;

            // Save user to localStorage
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
            localStorage.setItem(STORAGE_KEYS.AUTH_TIMESTAMP, Date.now().toString());

            return user;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Fetch user shops
export const fetchUserShops = createAsyncThunk(
    'auth/fetchUserShops',
    async (userId: number, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Shop[]>>(
                API_ENDPOINTS.USER_SHOPS(userId)
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Shop config (tax %, service charge %, requireCustomerInfo, ...)
export const fetchShopConfig = createAsyncThunk(
    'auth/fetchShopConfig',
    async (params: { userId: number; shopId: number }, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<ShopConfig>>(
                API_ENDPOINTS.SHOP_CONFIG(params.userId, params.shopId)
            );
            localStorage.setItem(STORAGE_KEYS.SHOP_CONFIG, JSON.stringify(response.data.data));
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Logout
export const logout = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            // Close POS session + unregister push while cookies are still valid
            await closePosSession();
            await api.post(API_ENDPOINTS.LOGOUT);

            // Clear localStorage
            localStorage.removeItem(STORAGE_KEYS.USER);
            localStorage.removeItem(STORAGE_KEYS.SELECTED_SHOP);
            localStorage.removeItem(STORAGE_KEYS.AUTH_TIMESTAMP);
            localStorage.removeItem(STORAGE_KEYS.CART_BACKUP);
            localStorage.removeItem(STORAGE_KEYS.SHOP_CONFIG);

            return;
        } catch (error) {
            // Even if API call fails, clear local state
            localStorage.clear();
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

/**
 * Auth Slice
 */
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        // Select shop
        selectShop: (state, action: PayloadAction<Shop>) => {
            state.selectedShop = action.payload;
            localStorage.setItem(STORAGE_KEYS.SELECTED_SHOP, JSON.stringify(action.payload));
            localStorage.setItem(STORAGE_KEYS.LAST_SHOP_ID, String(action.payload.shopId));
        },

        // Clear shop selection
        clearShopSelection: (state) => {
            state.selectedShop = null;
            state.shopConfig = null;
            localStorage.removeItem(STORAGE_KEYS.SELECTED_SHOP);
            localStorage.removeItem(STORAGE_KEYS.SHOP_CONFIG);
        },

        // Restore session from localStorage
        restoreSession: (state) => {
            const userStr = localStorage.getItem(STORAGE_KEYS.USER);
            const shopStr = localStorage.getItem(STORAGE_KEYS.SELECTED_SHOP);
            const timestamp = localStorage.getItem(STORAGE_KEYS.AUTH_TIMESTAMP);

            // Check if session is still valid (24 hours)
            if (timestamp) {
                const sessionAge = Date.now() - parseInt(timestamp);
                const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

                if (sessionAge > MAX_AGE) {
                    // Session expired
                    localStorage.clear();
                    return;
                }
            }

            if (userStr) {
                try {
                    state.user = JSON.parse(userStr);
                    state.isAuthenticated = true;
                } catch (e) {
                    console.error('Failed to parse user from localStorage', e);
                    localStorage.removeItem(STORAGE_KEYS.USER);
                }
            }

            if (shopStr) {
                try {
                    state.selectedShop = JSON.parse(shopStr);
                    const cfg = localStorage.getItem(STORAGE_KEYS.SHOP_CONFIG);
                    if (cfg) state.shopConfig = JSON.parse(cfg);
                } catch (e) {
                    console.error('Failed to parse shop from localStorage', e);
                    localStorage.removeItem(STORAGE_KEYS.SELECTED_SHOP);
                }
            }

            state.isInitialized = true;
        },

        // Clear error
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Generate Code
        builder
            .addCase(generateCode.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(generateCode.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(generateCode.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Login
        builder
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Fetch Shops
        builder
            .addCase(fetchUserShops.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserShops.fulfilled, (state, action) => {
                state.isLoading = false;
                state.shops = action.payload;

                // Auto-select if only one shop
                if (action.payload.length === 1 && !state.selectedShop) {
                    state.selectedShop = action.payload[0];
                    localStorage.setItem(STORAGE_KEYS.SELECTED_SHOP, JSON.stringify(action.payload[0]));
                }
            })
            .addCase(fetchUserShops.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        builder.addCase(fetchShopConfig.fulfilled, (state, action) => {
            state.shopConfig = action.payload;
        });

        // Logout
        builder
            .addCase(logout.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.selectedShop = null;
                state.shopConfig = null;
                state.shops = [];
                state.isAuthenticated = false;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(logout.rejected, (state) => {
                // Clear state even on error
                state.user = null;
                state.selectedShop = null;
                state.shops = [];
                state.isAuthenticated = false;
                state.isLoading = false;
            });
    },
});

export const { selectShop, clearShopSelection, restoreSession, clearError } = authSlice.actions;
export default authSlice.reducer;